import { useState } from 'react'
import { v4 as uuid } from 'uuid'

import type { CreateInventoryOrderItemReceiptInput } from '~/api/graphql/generated/types'

import {
  extractReceiptInputFromFields,
  useReceiptFields,
} from './useReceiptFields'

const DEFAULT_DRAFT_RECEIPT: CreateInventoryOrderItemReceiptInput = {
  receivedQuantity: null,
  lotNumber: null,
  expirationDate: null,
  serialNumber: null,
  damagedReasonId: null,
  damageNotes: null,
  businessReferenceNumber: null,
  rejected: false,
}

export const useDraftReceipts = () => {
  const [draftIds, setDraftIds] = useState<string[]>([])
  const {
    receiptFields: draftReceiptFields,
    validate,
    updateState,
  } = useReceiptFields(
    draftIds.map((id) => ({
      id,
      ...DEFAULT_DRAFT_RECEIPT,
    })),
  )

  const addDraftReceipt = () => {
    setDraftIds([...draftIds, uuid()])
  }

  const duplicateDraftReceipt = (
    sourceValues: CreateInventoryOrderItemReceiptInput,
  ) => {
    const newId = uuid()
    setDraftIds([...draftIds, newId])

    updateState({
      [`${newId}-receivedQuantity`]: sourceValues.receivedQuantity ?? null,
      [`${newId}-lotNumber`]: sourceValues.lotNumber ?? null,
      [`${newId}-expirationDate`]: sourceValues.expirationDate ?? null,
      [`${newId}-serialNumber`]: sourceValues.serialNumber ?? null,
      [`${newId}-businessReferenceNumber`]:
        sourceValues.businessReferenceNumber ?? null,
      [`${newId}-damageNotes`]: sourceValues.damageNotes ?? null,
      [`${newId}-damaged`]: Boolean(sourceValues.damagedReasonId),
      [`${newId}-damageReasonId`]: sourceValues.damagedReasonId ?? '',
    })
  }

  const deleteDraftReceipt = (id: string) => {
    setDraftIds(draftIds.filter((draftId) => draftId !== id))
  }

  const getDraftReceiptsForSubmission =
    (): CreateInventoryOrderItemReceiptInput[] =>
      draftReceiptFields.map(({ fields }) =>
        extractReceiptInputFromFields(fields),
      )

  return {
    draftReceiptFields,
    addDraftReceipt,
    duplicateDraftReceipt,
    deleteDraftReceipt,
    validateDraftReceipts: validate,
    getDraftReceiptsForSubmission,
  }
}
